"use client";

import { useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

/**
 * Le seuil de TVA arrive bien avant le plafond du régime micro.
 *
 * C'est la confusion numéro un du sujet, et elle coûte cher : un prestataire
 * persuadé d'avoir 83 600 € devant lui devient redevable de la TVA à 37 500 €,
 * soit à 45 % du chemin. Il l'apprend souvent après avoir facturé sans TVA
 * pendant des mois, et la TVA non facturée reste due.
 *
 * Une barre unique, deux repères posés dessus : le rapport se voit tout de
 * suite et c'est tout ce que la démo a à faire.
 *
 * Valeurs du référentiel arbitré du 05/09/2026, seuils applicables aux années
 * 2026, 2027 et 2028.
 */

const L = {
    fr: {
        title: "Le seuil de TVA tombe bien avant le plafond du régime",
        activity: "Activité",
        vente: "Vente de marchandises",
        services: "Prestations de services",
        ca: "Votre chiffre d'affaires",
        tva: "Seuil de TVA",
        tvaMaj: "Seuil majoré",
        zone: "Zone de tolérance",
        zoneHelp: "au-delà, la TVA s'applique le jour même",
        plafond: "Plafond du régime micro",
        stateFranchise: "Vous facturez sans TVA. Rien à déclarer de ce côté.",
        stateWatch: "Vous avez dépassé le seuil de franchise. La TVA s'appliquera au 1er janvier suivant si vous restez au-dessus.",
        stateTva: "Vous avez franchi le seuil majoré : la TVA devient due le jour même du dépassement, pas le mois suivant, pas l'année suivante.",
        stateOut: "Vous dépassez le plafond du régime micro. Le régime ne prend fin que si le dépassement se répète deux années de suite.",
        ratio: "Le seuil de TVA représente",
        ofPlafond: "du plafond",
        note: "Les deux seuils sont totalement indépendants : dépasser celui de la TVA ne vous fait pas quitter le régime micro, et rester sous le plafond ne vous dispense pas de la TVA. Ce sont deux réglementations distinctes qui se trouvent porter sur le même chiffre d'affaires.",
    },
    en: {
        title: "The VAT threshold hits long before the regime ceiling",
        activity: "Activity",
        vente: "Sale of goods",
        services: "Services",
        ca: "Your turnover",
        tva: "VAT threshold",
        tvaMaj: "Increased threshold",
        zone: "Tolerance band",
        zoneHelp: "beyond it, VAT applies the very same day",
        plafond: "Micro-regime ceiling",
        stateFranchise: "You invoice without VAT. Nothing to declare on that front.",
        stateWatch: "You have passed the exemption threshold. VAT will apply from 1 January next year if you stay above it.",
        stateTva: "You have crossed the increased threshold: VAT becomes due on the very day you cross it, not the following month, not the following year.",
        stateOut: "You are above the micro-regime ceiling. The regime only ends if you exceed it two years running.",
        ratio: "The VAT threshold sits at",
        ofPlafond: "of the ceiling",
        note: "The two thresholds are entirely independent: crossing the VAT one does not push you out of the micro regime, and staying under the ceiling does not exempt you from VAT. They are two separate sets of rules that happen to look at the same turnover.",
    },
};

const SEUILS = {
    vente: { tva: 85000, tvaMaj: 93500, plafond: 203100 },
    services: { tva: 37500, tvaMaj: 41250, plafond: 83600 },
};

const money = (v, lang) =>
    `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

export default function MeVatThresholds({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [activite, setActivite] = useState("services");
    const [ca, setCa] = useState(28000);

    const s = SEUILS[activite];
    const max = s.plafond * 1.15;
    const pos = (v) => Math.min(100, (v / max) * 100);
    const part = Math.round((s.tva / s.plafond) * 100);

    const REPERES = [
        { cle: "tva", v: s.tva, color: "#b45309" },
        { cle: "plafond", v: s.plafond, color: "#dc2626" },
    ];

    const etat =
        ca > s.plafond
            ? { texte: t.stateOut, tone: "bad" }
            : ca > s.tvaMaj
              ? { texte: t.stateTva, tone: "warn" }
              : ca > s.tva
                ? { texte: t.stateWatch, tone: "warn" }
                : { texte: t.stateFranchise, tone: "good" };

    return (
        <Frame icon="ruler-cross-pen-linear" title={t.title}>
            <div className="flex flex-wrap gap-2 mb-5">
                {["vente", "services"].map((cle) => (
                    <button
                        key={cle}
                        type="button"
                        onClick={() => setActivite(cle)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            activite === cle
                                ? "bg-primary text-white"
                                : "bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-lightBlue hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                        {t[cle]}
                    </button>
                ))}
            </div>

            <Slider
                label={t.ca}
                value={ca}
                onChange={setCa}
                min={0}
                max={Math.round(max)}
                step={500}
                display={money(ca, lang)}
            />

            <div className="relative mt-8 mb-2 pt-2">
                <div className="relative h-3 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
                    {/* Zone de tolérance : entre les deux seuils, la franchise
                        tient encore, mais elle ne tient plus qu'à un fil. La
                        montrer explique la mécanique des deux seuils mieux
                        qu'un trait de plus. */}
                    <div
                        className="absolute inset-y-0 bg-amber-500/25"
                        style={{
                            left: `${pos(s.tva)}%`,
                            width: `${pos(s.tvaMaj) - pos(s.tva)}%`,
                        }}
                    />
                    <div
                        className="absolute inset-y-0 left-0 bg-primary/85 transition-all duration-200"
                        style={{ width: `${pos(ca)}%` }}
                    />
                </div>

                {/* Les repères sont posés APRÈS la barre et la débordent en
                    hauteur : dessinés dedans, ils disparaissaient sous le
                    remplissage dès qu'on passait le seuil, c'est-à-dire
                    exactement quand ils comptent. */}
                {REPERES.map((m) => {
                    const x = pos(m.v);
                    // Près des bords, un libellé centré sort du cadre : il
                    // s'ancre alors sur le bord au lieu de le franchir.
                    const ancrage =
                        x > 78
                            ? "right-0 translate-x-0 text-right"
                            : x < 8
                              ? "left-0 translate-x-0"
                              : "-translate-x-1/2";
                    return (
                        <div
                            key={m.cle}
                            className={`absolute top-0 ${ancrage}`}
                            style={
                                x > 78
                                    ? { right: `${100 - x}%` }
                                    : { left: `${x}%` }
                            }
                        >
                            <div
                                className="w-0.5 h-7 rounded-full"
                                style={{ backgroundColor: m.color }}
                            />
                            <div
                                className="text-[11px] mt-1 whitespace-nowrap font-semibold"
                                style={{ color: m.color }}
                            >
                                {t[m.cle]}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-lightBlue/70 whitespace-nowrap tabular-nums">
                                {money(m.v, lang)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-baseline gap-2 mt-16 text-[11px] text-gray-500 dark:text-lightBlue/70">
                <span className="inline-block w-3 h-2.5 rounded-sm bg-amber-500/25 shrink-0" />
                <span>
                    <b className="text-gray-600 dark:text-lightBlue">
                        {t.zone}
                    </b>{" "}
                    {money(s.tva, lang)} - {money(s.tvaMaj, lang)},{" "}
                    {t.zoneHelp}
                </span>
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mb-0 mt-4">
                {t.ratio}{" "}
                <b className="dark:text-white">
                    {part} % {t.ofPlafond}
                </b>
            </p>

            <Verdict tone={etat.tone}>{etat.texte}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
